import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BriefcaseBusiness, Check, HelpCircle, ImageUp } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { SectionCard } from "@/components/workflow/common/SectionCard";

interface CompanyIdentityFormProps {
  generalInfo: any;
  updateGeneralInfo: (updates: any) => void;
  logoInputRef: React.RefObject<HTMLInputElement | null>;
  onLogoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLocked: boolean;
}

const fieldLabelClassName = "text-sm font-medium leading-5 text-slate-700";
const fieldInputClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-slate-300 disabled:cursor-default disabled:opacity-100";

export function CompanyIdentityForm({ generalInfo, updateGeneralInfo, logoInputRef, onLogoUpload, isLocked }: CompanyIdentityFormProps) {
  const { assessment } = useWizard();
  // Calculate assessment period dates from reporting year
  const startDate = assessment ? `${assessment.reportingYear}-01-01` : "";
  const endDate = assessment ? `${assessment.reportingYear}-12-31` : "";
  const companyName = assessment?.organizationName || generalInfo.companyName || "";
  const hasLogo = Boolean(generalInfo.companyLogoUrl);

  void updateGeneralInfo;

  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <BriefcaseBusiness className="h-5 w-5 flex-shrink-0 text-primary" />
      <h3 className="text-lg font-bold leading-7 text-slate-900">
        {"Company Identity"}
      </h3>
      <HelpCircle className="h-4 w-4 flex-shrink-0 text-slate-600" />
    </div>
  );

  return (
    <SectionCard header={header} data-testid="card-company-identity">
      <div className="space-y-6">
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Company Name"} <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder={"e.g. Malawi Sun Energy Ltd."}
            value={companyName}
            disabled
            data-testid="input-company-name"
            className={`${fieldInputClassName} bg-slate-50/60 text-slate-500`}
          />
        </div>

        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Assessment Period - Start Date"} <span className="text-red-500">*</span>
          </Label>
          <div className="grid grid-cols-1 items-center gap-2 md:grid-cols-[1fr_auto_1fr]">
            <Input
              type="date"
              value={startDate}
              disabled
              data-testid="input-assessment-start-date"
              className={fieldInputClassName}
            />
            <span className="text-base font-normal leading-6 text-slate-400">to</span>
            <Input
              type="date"
              value={endDate}
              disabled
              data-testid="input-assessment-end-date"
              className={fieldInputClassName}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Company Logo (optional)"}
          </Label>
          <input
            type="file"
            ref={logoInputRef}
            onChange={onLogoUpload}
            accept=".jpg,.jpeg,.png"
            className="hidden"
            disabled={isLocked}
          />
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            disabled={isLocked}
            data-testid="button-upload-logo"
            className="flex w-full flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {hasLogo ? (
              <>
                <Check className="h-8 w-8 text-[#00444C]" />
                <span className="mt-2 text-sm font-medium leading-5 text-slate-700">
                  {"Logo uploaded"}
                </span>
                <span className="pt-1 text-xs leading-4 text-slate-400">
                  JPEG or PNG only — max 10 MB
                </span>
              </>
            ) : (
              <>
                <ImageUp className="h-8 w-8 text-slate-400" />
                <span className="mt-2 text-sm font-normal leading-5 text-slate-400">
                  {"Upload Logo"}
                </span>
                <span className="pt-1 text-xs leading-4 text-slate-400">
                  JPEG or PNG only — max 10 MB
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}
